import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  HelpCircle, 
  MapPin, 
  ChevronDown, 
  ChevronRight,
  Star,
  BookOpen,
  Lightbulb,
  AlertCircle,
  CheckCircle2,
  Search,
  Filter,
  ThumbsUp,
  MessageSquare,
  ArrowLeft,
  Globe,
  Building2,
  Lock,
  Zap
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import DashboardLayout from "../components/DashboardLayout";
import { EarnMoneyBanner } from "../components/Client/PlanSection";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Interview questions database by consulate
const CONSULATES = [
  { id: "mexico_city", name: "Ciudad de México", country: "México", flag: "🇲🇽" },
  { id: "guadalajara", name: "Guadalajara", country: "México", flag: "🇲🇽" },
  { id: "monterrey", name: "Monterrey", country: "México", flag: "🇲🇽" },
  { id: "tijuana", name: "Tijuana", country: "México", flag: "🇲🇽" },
  { id: "hermosillo", name: "Hermosillo", country: "México", flag: "🇲🇽" },
  { id: "merida", name: "Mérida", country: "México", flag: "🇲🇽" },
  { id: "nogales", name: "Nogales", country: "México", flag: "🇲🇽" },
  { id: "ciudad_juarez", name: "Ciudad Juárez", country: "México", flag: "🇲🇽" },
  { id: "matamoros", name: "Matamoros", country: "México", flag: "🇲🇽" },
  { id: "nuevo_laredo", name: "Nuevo Laredo", country: "México", flag: "🇲🇽" },
  { id: "bogota", name: "Bogotá", country: "Colombia", flag: "🇨🇴" },
  { id: "lima", name: "Lima", country: "Perú", flag: "🇵🇪" },
  { id: "buenos_aires", name: "Buenos Aires", country: "Argentina", flag: "🇦🇷" },
  { id: "santiago", name: "Santiago", country: "Chile", flag: "🇨🇱" },
  { id: "sao_paulo", name: "São Paulo", country: "Brasil", flag: "🇧🇷" },
  { id: "caracas", name: "Caracas", country: "Venezuela", flag: "🇻🇪" },
  { id: "quito", name: "Quito", country: "Ecuador", flag: "🇪🇨" },
  { id: "guatemala", name: "Ciudad de Guatemala", country: "Guatemala", flag: "🇬🇹" },
  { id: "san_salvador", name: "San Salvador", country: "El Salvador", flag: "🇸🇻" },
  { id: "tegucigalpa", name: "Tegucigalpa", country: "Honduras", flag: "🇭🇳" },
  { id: "managua", name: "Managua", country: "Nicaragua", flag: "🇳🇮" },
  { id: "san_jose", name: "San José", country: "Costa Rica", flag: "🇨🇷" },
  { id: "panama", name: "Panamá", country: "Panamá", flag: "🇵🇦" },
  { id: "santo_domingo", name: "Santo Domingo", country: "Rep. Dominicana", flag: "🇩🇴" },
  { id: "madrid", name: "Madrid", country: "España", flag: "🇪🇸" }
];

const QUESTION_CATEGORIES = [
  { id: "purpose", name: "Propósito del viaje", icon: HelpCircle, color: "blue" },
  { id: "ties", name: "Lazos con tu país", icon: Building2, color: "green" },
  { id: "financial", name: "Situación económica", icon: Star, color: "amber" },
  { id: "travel_history", name: "Historial de viajes", icon: Globe, color: "purple" },
  { id: "family", name: "Familia y relaciones", icon: MapPin, color: "rose" },
  { id: "employment", name: "Empleo y educación", icon: BookOpen, color: "indigo" },
  { id: "specific", name: "Preguntas específicas", icon: AlertCircle, color: "orange" }
];

// Questions data with consulate-specific notes
const QUESTIONS_DATABASE = {
  purpose: [
    {
      id: 1,
      question: "¿Cuál es el propósito de su viaje a Estados Unidos?",
      frequency: "muy_frecuente",
      tips: [
        "Sé específico y claro",
        "Menciona fechas concretas si las tienes",
        "No divagues ni des información innecesaria"
      ],
      goodAnswer: "Voy a visitar a mi primo que vive en Miami por dos semanas. Quiero conocer los parques de Orlando y descansar en la playa.",
      badAnswer: "Quiero conocer Estados Unidos porque siempre lo he soñado.",
      consulateNotes: {
        mexico_city: "Esta pregunta es siempre la primera. Sé muy específico.",
        guadalajara: "Suelen pedir detalles del itinerario.",
        bogota: "Muy enfáticos en verificar el propósito real."
      }
    },
    {
      id: 2,
      question: "¿Dónde se va a hospedar?",
      frequency: "muy_frecuente",
      tips: [
        "Ten el nombre del hotel o dirección de tu anfitrión",
        "Si vas con familia, menciona la relación",
        "Lleva confirmación de reservación si tienes"
      ],
      goodAnswer: "Me voy a quedar en el hotel Marriott en el centro de Nueva York. Ya tengo mi reservación confirmada.",
      badAnswer: "Todavía no sé, voy a ver cuando llegue."
    },
    {
      id: 3,
      question: "¿Cuánto tiempo planea estar en Estados Unidos?",
      frequency: "muy_frecuente",
      tips: [
        "Da un tiempo específico y razonable",
        "Que coincida con tu propósito",
        "Máximo 2-3 semanas para turismo es lo ideal"
      ],
      goodAnswer: "Planeo estar 12 días, del 15 al 27 de marzo.",
      badAnswer: "No sé, entre uno y tres meses, dependiendo."
    },
    {
      id: 4,
      question: "¿Ha visitado Estados Unidos antes?",
      frequency: "frecuente",
      tips: [
        "Si sí, menciona cuándo y por cuánto tiempo",
        "Demuestra que respetaste las condiciones de tu visa anterior",
        "Si no, no te preocupes, todos empiezan alguna vez"
      ],
      goodAnswer: "Sí, fui en 2019 a California por 10 días. Regresé antes de que venciera mi tiempo autorizado.",
      badAnswer: "No, pero mi sueño siempre ha sido vivir allá."
    }
  ],
  ties: [
    {
      id: 5,
      question: "¿Qué lo motiva a regresar a su país?",
      frequency: "muy_frecuente",
      tips: [
        "Menciona lazos concretos: trabajo, familia, propiedades",
        "Sé genuino y específico",
        "Evita respuestas genéricas"
      ],
      goodAnswer: "Tengo mi negocio de ferretería que llevo 15 años manejando, mi esposa e hijos viven aquí, y estamos construyendo nuestra casa.",
      badAnswer: "Mi familia está aquí y me gusta mi país.",
      consulateNotes: {
        mexico_city: "Pregunta crucial. Ten documentos listos.",
        tijuana: "Muy estrictos con esta pregunta por la cercanía con la frontera."
      }
    },
    {
      id: 6,
      question: "¿Es dueño de alguna propiedad?",
      frequency: "frecuente",
      tips: [
        "Si tienes propiedad, lleva las escrituras",
        "Si rentas, no es problema pero menciona tu contrato",
        "Propiedades familiares también cuentan"
      ],
      goodAnswer: "Sí, tengo un departamento a mi nombre desde 2018, y un terreno que heredé de mi padre.",
      badAnswer: "No tengo nada, vivo con mis padres."
    },
    {
      id: 7,
      question: "¿Tiene hijos? ¿Dónde están?",
      frequency: "frecuente",
      tips: [
        "Si tienes hijos que se quedan, es un lazo fuerte",
        "Menciona sus edades y situación escolar",
        "Llevar fotos NO es necesario pero puedes mencionarlos"
      ],
      goodAnswer: "Tengo dos hijos de 8 y 12 años. Están en la escuela aquí y se quedan con mi esposa.",
      badAnswer: "Sí, pero van a viajar conmigo."
    }
  ],
  financial: [
    {
      id: 8,
      question: "¿Quién va a pagar su viaje?",
      frequency: "muy_frecuente",
      tips: [
        "Si tú pagas, ten estados de cuenta listos",
        "Si alguien más paga, explica la relación claramente",
        "Muestra solvencia económica"
      ],
      goodAnswer: "Yo voy a pagar el viaje con mis ahorros. Trabajo como gerente de ventas y tengo un ingreso mensual de $3,000 dólares.",
      badAnswer: "Un amigo que conocí por internet me va a pagar todo."
    },
    {
      id: 9,
      question: "¿Cuánto gana mensualmente?",
      frequency: "frecuente",
      tips: [
        "Sé honesto, pueden verificar",
        "Incluye todas tus fuentes de ingreso",
        "Ten tu carta laboral con el salario"
      ],
      goodAnswer: "Gano $2,500 dólares mensuales como contador. Tengo esta carta de mi empresa que lo confirma.",
      badAnswer: "No sé exactamente, como unos... varios miles."
    },
    {
      id: 10,
      question: "¿Cuánto dinero va a llevar?",
      frequency: "frecuente",
      tips: [
        "Ten un presupuesto realista",
        "No necesitas llevar mucho efectivo si usas tarjeta",
        "Calcula ~$150-200 USD por día como referencia"
      ],
      goodAnswer: "Tengo un presupuesto de $3,000 dólares para los 12 días. Voy a usar mi tarjeta de crédito principalmente.",
      badAnswer: "Todo el dinero que pueda, no sé cuánto."
    }
  ],
  travel_history: [
    {
      id: 11,
      question: "¿Tiene familiares en Estados Unidos?",
      frequency: "muy_frecuente",
      tips: [
        "Sé honesto, mentir es causa de rechazo permanente",
        "Menciona el estatus migratorio si lo sabes",
        "No pasa nada si tienes familia allá"
      ],
      goodAnswer: "Sí, tengo un primo que es residente legal. Vive en Houston y trabaja en construcción. Lo voy a visitar.",
      badAnswer: "No, nadie. (Si realmente tienes familia allá)",
      consulateNotes: {
        ciudad_juarez: "Pregunta MUY importante. Siempre dicen la verdad.",
        tijuana: "Verifican cruzando información con otras entrevistas."
      }
    },
    {
      id: 12,
      question: "¿Ha viajado a otros países?",
      frequency: "frecuente",
      tips: [
        "Menciona todos los viajes internacionales",
        "Demuestra que eres un viajero responsable",
        "Trae tu pasaporte anterior si tiene sellos"
      ],
      goodAnswer: "Sí, he viajado a Canadá en 2020 y a España en 2022. En ambos casos regresé a tiempo.",
      badAnswer: "No, este sería mi primer viaje internacional."
    },
    {
      id: 13,
      question: "¿Le han negado una visa antes?",
      frequency: "muy_frecuente",
      tips: [
        "NUNCA mientas sobre esto",
        "Si te negaron, explica qué ha cambiado",
        "Una negación previa no significa rechazo automático"
      ],
      goodAnswer: "Sí, me la negaron en 2018 porque no pude demostrar lazos económicos. Desde entonces conseguí un mejor trabajo y compré mi casa.",
      badAnswer: "No. (Si realmente te la negaron antes)"
    }
  ],
  family: [
    {
      id: 14,
      question: "¿Viaja solo o acompañado?",
      frequency: "frecuente",
      tips: [
        "Si viajas con familia, explica quiénes son",
        "Cada persona debe tener su propia entrevista",
        "Viajar en familia puede ser positivo"
      ],
      goodAnswer: "Viajo con mi esposa. Ella está haciendo su entrevista en la ventanilla de al lado.",
      badAnswer: "Con un grupo de amigos que conocí hace poco."
    },
    {
      id: 15,
      question: "¿Está casado? ¿Dónde está su esposo/a?",
      frequency: "frecuente",
      tips: [
        "Si tu pareja se queda, es un lazo fuerte",
        "Si viajan juntos, explícalo",
        "Tener familia es generalmente positivo"
      ],
      goodAnswer: "Sí, estoy casada hace 10 años. Mi esposo se queda trabajando, él es médico y no puede dejar sus pacientes.",
      badAnswer: "Sí, pero nos estamos separando."
    }
  ],
  employment: [
    {
      id: 16,
      question: "¿En qué trabaja?",
      frequency: "muy_frecuente",
      tips: [
        "Describe tu trabajo claramente",
        "Menciona cuánto tiempo llevas ahí",
        "Ten tu carta laboral lista"
      ],
      goodAnswer: "Soy ingeniero de sistemas en una empresa de tecnología. Llevo 5 años trabajando ahí como desarrollador senior.",
      badAnswer: "Tengo varios trabajos... hago de todo un poco."
    },
    {
      id: 17,
      question: "¿Cuánto tiempo de vacaciones le dieron?",
      frequency: "frecuente",
      tips: [
        "Debe coincidir con tu tiempo de viaje",
        "Ten una carta de tu trabajo autorizando",
        "Sé específico con las fechas"
      ],
      goodAnswer: "Me dieron dos semanas de vacaciones, del 15 al 30 de marzo. Tengo esta carta firmada por mi jefe.",
      badAnswer: "Me dieron vacaciones indefinidas."
    },
    {
      id: 18,
      question: "¿Qué estudiaste?",
      frequency: "frecuente",
      tips: [
        "Menciona tu nivel de estudios",
        "Si estudiaste algo relacionado con tu trabajo actual, menciónalo",
        "Estudios técnicos también cuentan"
      ],
      goodAnswer: "Estudié Administración de Empresas en la Universidad Nacional. Me gradué en 2015.",
      badAnswer: "No terminé la escuela."
    }
  ],
  specific: [
    {
      id: 19,
      question: "¿Por qué quiere ir específicamente a [ciudad]?",
      frequency: "frecuente",
      tips: [
        "Ten razones específicas para tu destino",
        "Investiga qué hay en esa ciudad",
        "Que tenga sentido con tu propósito"
      ],
      goodAnswer: "Quiero ir a San Francisco porque mi primo vive ahí y quiero conocer el Golden Gate Bridge y Alcatraz.",
      badAnswer: "No sé, simplemente quiero conocer Estados Unidos.",
      consulateNotes: {
        guadalajara: "Muy comunes las preguntas sobre destino específico."
      }
    },
    {
      id: 20,
      question: "Si le niego la visa hoy, ¿qué haría?",
      frequency: "ocasional",
      tips: [
        "Responde con calma",
        "Demuestra que tu vida sigue sin la visa",
        "No actúes desesperado"
      ],
      goodAnswer: "Sería una lástima, pero regresaría a mi trabajo normal y quizás intente de nuevo más adelante cuando mi situación mejore.",
      badAnswer: "No sé qué haría, realmente necesito esa visa."
    }
  ]
};

// Question Card Component
const QuestionCard = ({ question, category, consulate, expanded, onToggle }) => {
  const frequencyColors = {
    muy_frecuente: "bg-red-100 text-red-700 border-red-200",
    frecuente: "bg-amber-100 text-amber-700 border-amber-200",
    ocasional: "bg-blue-100 text-blue-700 border-blue-200"
  };

  const frequencyLabels = {
    muy_frecuente: "Muy frecuente",
    frecuente: "Frecuente",
    ocasional: "Ocasional"
  };

  const consulateNote = question.consulateNotes?.[consulate];

  return (
    <motion.div
      layout
      className="bg-white rounded-xl border border-navy-100 overflow-hidden hover:border-navy-200 transition-colors"
    >
      <button
        onClick={onToggle}
        className="w-full p-4 text-left flex items-start justify-between gap-4"
      >
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className={`text-xs px-2 py-0.5 rounded-full border ${frequencyColors[question.frequency]}`}>
              {frequencyLabels[question.frequency]}
            </span>
            {consulateNote && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200">
                Nota del consulado
              </span>
            )}
          </div>
          <p className="font-medium text-navy-900">{question.question}</p>
        </div>
        <ChevronDown className={`w-5 h-5 text-navy-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-navy-100"
          >
            <div className="p-4 space-y-4">
              {/* Consulate specific note */}
              {consulateNote && (
                <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                  <div className="flex items-center gap-2 text-purple-700 font-medium text-sm mb-1">
                    <MapPin className="w-4 h-4" />
                    Nota específica del consulado
                  </div>
                  <p className="text-sm text-purple-900">{consulateNote}</p>
                </div>
              )}

              {/* Tips */}
              <div>
                <h4 className="text-sm font-medium text-navy-700 mb-2 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-amber-500" />
                  Consejos
                </h4>
                <ul className="space-y-1">
                  {question.tips.map((tip, i) => (
                    <li key={i} className="text-sm text-navy-600 flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Good Answer */}
              <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                <h4 className="text-sm font-medium text-emerald-700 mb-1 flex items-center gap-2">
                  <ThumbsUp className="w-4 h-4" />
                  Ejemplo de buena respuesta
                </h4>
                <p className="text-sm text-emerald-900 italic">"{question.goodAnswer}"</p>
              </div>

              {/* Bad Answer */}
              <div className="bg-red-50 rounded-lg p-3 border border-red-100">
                <h4 className="text-sm font-medium text-red-700 mb-1 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Evita respuestas como esta
                </h4>
                <p className="text-sm text-red-900 italic">"{question.badAnswer}"</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const InterviewQuestions = () => {
  const { user, isPremium, isTrialMode } = useAuth();
  const [selectedConsulate, setSelectedConsulate] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedQuestions, setExpandedQuestions] = useState([]);
  const [showAllConsulates, setShowAllConsulates] = useState(false);

  // Check if user is in trial mode
  const isTrialUser = isTrialMode?.() || user?.plan_type === 'prueba';

  // Get all questions flat
  const allQuestions = Object.entries(QUESTIONS_DATABASE).flatMap(([catId, questions]) => 
    questions.map(q => ({ ...q, categoryId: catId }))
  );

  // Filter questions
  const filteredQuestions = allQuestions.filter(q => {
    if (selectedCategory !== "all" && q.categoryId !== selectedCategory) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!q.question.toLowerCase().includes(query) && 
          !q.goodAnswer.toLowerCase().includes(query) &&
          !q.tips.some(t => t.toLowerCase().includes(query))) {
        return false;
      }
    }
    return true;
  });

  // Sort by frequency
  const sortedQuestions = [...filteredQuestions].sort((a, b) => {
    const order = { muy_frecuente: 0, frecuente: 1, ocasional: 2 };
    return order[a.frequency] - order[b.frequency];
  });

  const toggleQuestion = (id) => {
    setExpandedQuestions(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const displayedConsulates = showAllConsulates ? CONSULATES : CONSULATES.slice(0, 10);

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Link to="/dashboard" className="inline-flex items-center gap-2 text-navy-600 hover:text-navy-900 mb-4">
            <ArrowLeft className="w-4 h-4" />
            Volver al Dashboard
          </Link>
          <h1 className="font-heading text-3xl font-bold text-navy-900 mb-2">
            {isTrialUser ? "Directorio de Consulados" : "Banco de Preguntas de Entrevista"}
          </h1>
          <p className="text-navy-600">
            {isTrialUser 
              ? "Consulados de Estados Unidos en Latinoamérica y España"
              : "Preguntas frecuentes en entrevistas de visa B1/B2 con consejos específicos por consulado"
            }
          </p>
        </motion.div>

        {/* Consulate Selector - Available for ALL users */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card-elevated p-6"
        >
          <h2 className="font-semibold text-navy-900 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-gold-600" />
            {isTrialUser ? "Consulados Disponibles" : "Selecciona tu Consulado"}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {displayedConsulates.map(consulate => (
              <button
                key={consulate.id}
                onClick={() => !isTrialUser && setSelectedConsulate(consulate.id)}
                className={`p-3 rounded-lg text-left transition-all ${
                  isTrialUser 
                    ? "bg-navy-50 border-2 border-transparent cursor-default"
                    : selectedConsulate === consulate.id
                      ? "bg-gold-100 border-2 border-gold-500"
                      : "bg-navy-50 border-2 border-transparent hover:border-navy-200"
                }`}
              >
                <span className="text-lg">{consulate.flag}</span>
                <p className="text-sm font-medium text-navy-900 mt-1">{consulate.name}</p>
                <p className="text-xs text-navy-500">{consulate.country}</p>
              </button>
            ))}
          </div>
          {CONSULATES.length > 10 && (
            <button
              onClick={() => setShowAllConsulates(!showAllConsulates)}
              className="mt-4 text-sm text-gold-600 hover:text-gold-700 font-medium"
            >
              {showAllConsulates ? "Ver menos" : `Ver todos (${CONSULATES.length})`}
            </button>
          )}
        </motion.div>

        {/* Trial User Block - Show upgrade message instead of questions */}
        {isTrialUser ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card-elevated p-8 text-center"
          >
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-amber-600" />
            </div>
            <h3 className="text-xl font-bold text-navy-900 mb-2">
              Banco de Preguntas Bloqueado
            </h3>
            <p className="text-navy-600 mb-6 max-w-md mx-auto">
              El banco de preguntas con tips y respuestas modelo está disponible para usuarios con cuenta activa.
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6 max-w-md mx-auto text-left">
              <p className="font-medium text-emerald-800 mb-2 flex items-center gap-2">
                <Zap className="w-4 h-4" />
                Al activar tu cuenta obtienes:
              </p>
              <ul className="text-sm text-emerald-700 space-y-1">
                <li>• +50 preguntas frecuentes de entrevista</li>
                <li>• Tips específicos por consulado</li>
                <li>• Respuestas modelo para cada pregunta</li>
                <li>• Notas sobre qué evitar decir</li>
              </ul>
            </div>
            <Button
              onClick={() => window.open("https://wa.me/+525555555555?text=Hola, quiero activar mi cuenta de Divídelo", "_blank")}
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
            >
              <Zap className="w-4 h-4 mr-2" />
              Activar mi cuenta
            </Button>
          </motion.div>
        ) : (
          <>
            {/* Filters - Only for active users */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-elevated p-4"
        >
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-400" />
              <Input
                placeholder="Buscar preguntas..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-full sm:w-56">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las categorías</SelectItem>
                {QUESTION_CATEGORIES.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </motion.div>

        {/* Categories Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
        >
          {QUESTION_CATEGORIES.map(cat => {
            const count = QUESTIONS_DATABASE[cat.id]?.length || 0;
            const IconComponent = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(selectedCategory === cat.id ? "all" : cat.id)}
                className={`p-4 rounded-xl text-left transition-all ${
                  selectedCategory === cat.id
                    ? `bg-${cat.color}-100 border-2 border-${cat.color}-300`
                    : "bg-white border-2 border-navy-100 hover:border-navy-200"
                }`}
              >
                <div className={`w-8 h-8 rounded-lg bg-${cat.color}-100 flex items-center justify-center mb-2`}>
                  <IconComponent className={`w-4 h-4 text-${cat.color}-600`} />
                </div>
                <p className="font-medium text-navy-900 text-sm">{cat.name}</p>
                <p className="text-xs text-navy-500">{count} preguntas</p>
              </button>
            );
          })}
        </motion.div>

        {/* Questions List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-3"
        >
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-navy-900">
              {sortedQuestions.length} preguntas
              {selectedCategory !== "all" && ` en "${QUESTION_CATEGORIES.find(c => c.id === selectedCategory)?.name}"`}
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpandedQuestions(
                expandedQuestions.length === sortedQuestions.length 
                  ? [] 
                  : sortedQuestions.map(q => q.id)
              )}
            >
              {expandedQuestions.length === sortedQuestions.length ? "Colapsar todo" : "Expandir todo"}
            </Button>
          </div>

          {sortedQuestions.map(question => (
            <QuestionCard
              key={question.id}
              question={question}
              category={QUESTION_CATEGORIES.find(c => c.id === question.categoryId)}
              consulate={selectedConsulate}
              expanded={expandedQuestions.includes(question.id)}
              onToggle={() => toggleQuestion(question.id)}
            />
          ))}

          {sortedQuestions.length === 0 && (
            <div className="text-center py-12 bg-navy-50 rounded-xl">
              <HelpCircle className="w-12 h-12 text-navy-300 mx-auto mb-3" />
              <p className="text-navy-500">No se encontraron preguntas</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => {
                  setSearchQuery("");
                  setSelectedCategory("all");
                }}
              >
                Limpiar filtros
              </Button>
            </div>
          )}
        </motion.div>

        {/* Practice CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="card-elevated p-6 bg-gradient-to-r from-gold-50 to-amber-50 border-gold-200"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-heading text-lg font-bold text-navy-900 mb-1">
                ¿Listo para practicar?
              </h3>
              <p className="text-sm text-navy-600">
                Usa nuestro simulador de entrevista con IA para practicar estas preguntas
              </p>
            </div>
            <Link to="/interview-simulator">
              <Button className="bg-gold-600 hover:bg-gold-700 text-white">
                <MessageSquare className="w-4 h-4 mr-2" />
                Ir al Simulador
              </Button>
            </Link>
          </div>
        </motion.div>
        </>
        )}

        {/* Earn Money Banner - Only for clients */}
        <div className="mt-8">
          <EarnMoneyBanner userRole={user?.role} />
        </div>
      </div>
    </DashboardLayout>
  );
};

export default InterviewQuestions;
